import { Box, Flex, Image } from '@energiebespaarders/symbols';
import { useIsMobile, useIsTablet } from '@energiebespaarders/symbols/hooks';
import mediaMin from '@energiebespaarders/symbols/styles/breakpoints';
import { tint } from '@energiebespaarders/symbols/styles/mixins';
import React, { useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { shuffleArray } from '~/utils/shuffleArray';

import ATAG from '/public/img/logos/ATAG.svg';
import BASF from '/public/img/logos/BASF.svg';
import Daikin from '/public/img/logos/Daikin.svg';
import Hyundai from '/public/img/logos/Hyundai.svg';
import Intergas from '/public/img/logos/Intergas.svg';
import Kingspan from '/public/img/logos/Kingspan.svg';
import Knauf from '/public/img/logos/KnaufInsulation.svg';
import LG from '/public/img/logos/LG.svg';
import Neopixels from '/public/img/logos/Neopixels.svg';
import Remeha from '/public/img/logos/Remeha.svg';
import SMA from '/public/img/logos/SMA.svg';
import SolarEdge from '/public/img/logos/SolarEdge.svg';
import Tonzon from '/public/img/logos/Tonzon.svg';
import TrinaSolar from '/public/img/logos/TrinaSolar.svg';
import Vaillant from '/public/img/logos/Vaillant.svg';

const defaultShowcase = [
  Hyundai,
  ATAG,
  Vaillant,
  Daikin,
  Tonzon,
  LG,
  SolarEdge,
  Remeha,
  TrinaSolar,
  BASF,
  Kingspan,
  Knauf,
  Intergas,
  SMA,
  Neopixels,
];

interface LogoBubblesProps {
  logoImageSources?: string[];
}

const StyledFlex = styled(Flex)`
  position: relative;
  overflow-y: visible;
  overflow-x: hidden;
  flex-wrap: wrap;
  left: -15px;

  ${mediaMin.lg} {
    overflow: visible;
    flex-wrap: wrap;
    left: 0;
  }
`;

const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;

const bubbleAnimation = (randomSeed: number) => keyframes`
  0% {
    transform: scale(1, 1) translate(0, 0);
  }
  33% {
    transform: scale(${randomInRange(0.97, 1.03)}, ${randomInRange(
      0.97,
      1.03,
    )}) translate(calc(${randomSeed} * 4px), calc(${randomSeed} * 6px));
  }
  66% {
    transform: scale(${randomInRange(0.9, 1.1)}, ${randomInRange(
      0.97,
      1.03,
    )}) translate(calc(${randomSeed} * 6px), calc(${randomSeed} * 4px));
  }
  100% {
    transform: scale(1, 1) translate(0, 0);
  }
`;

const StyledBubble = styled(Flex)<{ randomSeed: number }>`
  position: relative;
  background: white;
  top: ${x => x.randomSeed * 12}px;
  left: ${x => x.randomSeed * 12}px;
  right: ${x => x.randomSeed * 12}px;
  border: 1px solid ${x => tint(0.33, x.theme.colors.gold)};
  border-radius: 100%;
  width: 90px;
  height: 90px;
  animation: ${x => bubbleAnimation(x.randomSeed)} 5s infinite;

  ${mediaMin.sm} {
    width: 120px;
    height: 120px;
    top: ${x => x.randomSeed * 30}px;
    left: ${x => x.randomSeed * 30}px;
    right: ${x => x.randomSeed * 30}px;
  }
`;

const LogoBubbles: React.FC<LogoBubblesProps> = ({ logoImageSources = defaultShowcase }) => {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const amountToShow = useMemo(() => (isMobile ? 8 : isTablet ? 11 : 12), [isMobile, isTablet]);
  const rendered = useMemo(
    () => [...new Set(shuffleArray(logoImageSources))].slice(0, amountToShow),
    [logoImageSources, amountToShow],
  );
  return (
    <StyledFlex justifyContent="space-around" mx={-3} py={5}>
      {rendered.map((src, i) => (
        <Box
          key={`logo-${src}-${i}`}
          width={[1 / 4, 1 / 5, 1 / 6]}
          px={Math.random() * 5}
          pb={Math.random() * 5}
        >
          <StyledBubble
            alignItems="center"
            justifyContent="center"
            randomSeed={Math.random()}
            p={2}
          >
            <Image src={src} alt={src} width="100%" />
          </StyledBubble>
        </Box>
      ))}
    </StyledFlex>
  );
};

export default LogoBubbles;
